import { IonIcon } from '@ionic/react';
import { carOutline } from 'ionicons/icons';
import type { OrderStatus } from '../../types/order';
import { STEP_ICON, STEP_COLOR } from './orderStepVisuals';

/**
 * Order counts bucketed into the four canonical progress steps. `completed` is
 * shown here even though "done" orders are excluded from the listing's
 * active-order headline count.
 */
export interface StepCounts {
  paid: number;
  accepted: number;
  ready: number;
  completed: number;
}

// The four columns, left → right. 'ready' borrows ready_to_pickup's visuals
// (bag icon / tertiary color).
const TALLY_STEPS: { key: keyof StepCounts; status: OrderStatus }[] = [
  { key: 'paid', status: 'paid' },
  { key: 'accepted', status: 'accepted' },
  { key: 'ready', status: 'ready_to_pickup' },
  { key: 'completed', status: 'completed' },
];

/**
 * A compact, always-filled variant of {@link OrderStatusBreadcrumb}: every step
 * circle is small and rendered in its brand color (this isn't tracking one
 * order's position — it's a summary), with the number of orders currently
 * sitting on each step printed above it.
 */
export const ListingOrderStepTally: React.FC<{
  counts: StepCounts;
  /** Every order on the "ready" step is a drop-off out for delivery, so the
   *  step shows the car rather than the pickup bag. */
  readyIsDropoff?: boolean;
}> = ({ counts, readyIsDropoff = false }) => {
  return (
    <div className="flex items-start">
      {TALLY_STEPS.map((step, i) => {
        const color = STEP_COLOR[step.status];
        const count = counts[step.key];
        const nextColor = STEP_COLOR[TALLY_STEPS[i + 1]?.status];
        return (
          <div key={step.key} className="flex flex-1 flex-col items-center">
            {/* Count of orders on this step */}
            <span
              className={`text-xs font-semibold leading-none ${
                count > 0 ? color?.text : 'text-muted-foreground'
              }`}
            >
              {count}
            </span>

            <div className="relative mt-1 flex h-6 w-full items-center">
              {/* Left connector — always filled (every step is "reached" in a tally) */}
              {i > 0 ? (
                <div className={`h-0.5 flex-1 ${color?.line}`} />
              ) : (
                <div className="flex-1" />
              )}

              {/* Spacer reserving the circle's footprint on the row */}
              <div className="w-6 shrink-0" />

              {/* Small filled circle with the step icon, centered on the line */}
              <div
                className={`absolute left-1/2 top-1/2 flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full ${color?.bg}`}
              >
                <IonIcon
                  icon={step.key === 'ready' && readyIsDropoff ? carOutline : STEP_ICON[step.status]}
                  className="text-[0.65rem] text-white"
                />
              </div>

              {/* Right connector — colored with the next step */}
              {i < TALLY_STEPS.length - 1 ? (
                <div className={`h-0.5 flex-1 ${nextColor?.line}`} />
              ) : (
                <div className="flex-1" />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
